import { cs } from '../../lib/currency';
/**
 * Customer Billing Portal
 *
 * Gated behind the `billing_portal` feature toggle (admin enables per
 * tenant). Lets customers:
 *   - View every lane-booking invoice they have (paid + outstanding).
 *   - See totals (total billed, paid, outstanding).
 *   - Click "Pay Now" on any unpaid row → Stripe Checkout redirect.
 *   - Download a receipt for paid rows (the Stripe-hosted receipt URL).
 *
 * Lane bookings are the only thing customers buy from us today, so this
 * page is intentionally focused. If we later sell other items to the
 * customer role, the rows array becomes union-typed.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Receipt, Loader2, CreditCard,
  ExternalLink, DollarSign, ArrowRight, Building2, Wallet, Lock, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

import api from '../../services/api';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { ParentInvoiceList } from '../../components/parent/ParentInvoiceList';
import { InvoicePayDialog } from '../../components/parent/InvoicePayDialog';
import PaymentMethodOnFileForm from '../../components/PaymentMethodOnFileForm';
import { useFeatures } from '../../context/FeatureContext';

const formatMoney = (n) => `${cs()}${(Number(n) || 0).toFixed(2)}`;
export default function CustomerBilling() {
  const [searchParams] = useSearchParams();
  const { isFeatureEnabled } = useFeatures();
  const achEnabled = isFeatureEnabled('payments.ach');

  // iter262 — Pay All Outstanding banner state. Uses the unified
  // /api/billing/outstanding-summary endpoint which covers ALL invoice
  // types (lane + enrollment + tournament + small-group + camp + manual),
  // not just lane bookings. We surface a banner when 2+ are pending so the
  // customer can clear everything in a single Stripe checkout.
  const [payAllSummary, setPayAllSummary] = useState({ outstanding_total: 0, outstanding_count: 0 });
  const [payAllLoading, setPayAllLoading] = useState(false);
  const [payAllPmChoice, setPayAllPmChoice] = useState(null);
  const [showPayAllDialog, setShowPayAllDialog] = useState(false);

  // iter266 — fetch saved PMs so we can show "Pays from VISA ••1234"
  // inline on the ACH/Card buttons in the Pay All dialog.
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [openingPaymentMethod, setOpeningPaymentMethod] = useState(false);
  const [setupIntent, setSetupIntent] = useState(null);
  const savedCard = (
    paymentMethods.find((pm) => pm.type === 'card' && pm.is_default) ||
    paymentMethods.find((pm) => pm.type === 'card')
  );
  const savedAch = (
    paymentMethods.find((pm) => pm.type === 'bank_account' && pm.is_default) ||
    paymentMethods.find((pm) => pm.type === 'bank_account')
  );

  const fetchPaymentMethods = async () => {
    try {
      const { data } = await api.get('/stripe/payment-methods');
      const _pms = data?.payment_methods || [];
      setPaymentMethods(_pms);
      // iter375 — "one payment method required" enforcement (alert layer).
      if (_pms.length === 0 && data?.has_stripe_customer !== false) {
        api.post('/stripe/payment-methods/required-check').catch(() => {});
      }
    } catch {
      // silent — chips just won't show.
    }
  };

  const beginPaymentMethodSetup = async (paymentMethodType = 'card') => {
    setOpeningPaymentMethod(true);
    try {
      const { data } = await api.post('/stripe/payment-methods/setup-intent', {
        payment_method_type: paymentMethodType,
      });
      setSetupIntent(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start the secure payment form');
    } finally {
      setOpeningPaymentMethod(false);
    }
  };

  const confirmPaymentMethodSetup = async (setupIntentId, paymentMethodId) => {
    const { data } = await api.post('/stripe/payment-methods/confirm-setup-intent', {
      setup_intent_id: setupIntentId,
      payment_method_id: paymentMethodId,
    });
    setPaymentMethods(data.payment_methods || []);
    setSetupIntent(null);
    toast.success('Payment method saved securely');
  };

  const removePaymentMethod = async (paymentMethodId) => {
    try {
      const { data } = await api.delete(`/stripe/payment-methods/${paymentMethodId}`);
      setPaymentMethods(data.payment_methods || []);
      toast.success('Payment method removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'This payment method could not be removed');
    }
  };

  const handleSetDefaultPm = async (pmId) => {
    try {
      await api.post(`/stripe/set-default-payment-method/${pmId}`);
      toast.success('Preferred payment method updated');
      await fetchPaymentMethods();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update preferred method');
    }
  };

  const fetchPayAllSummary = async () => {
    try {
      const { data } = await api.get('/billing/outstanding-summary');
      setPayAllSummary({
        outstanding_total: Number(data?.outstanding_total || 0),
        outstanding_count: Number(data?.outstanding_count || 0),
      });
    } catch {
      // Silent — banner just won't show. Feature is opt-in nudge.
    }
  };

  const handlePayAll = async (pmChoice) => {
    setPayAllLoading(true);
    try {
      const { data } = await api.post('/billing/pay-all-outstanding', {
        return_url: window.location.origin + '/customer/billing',
        payment_method_choice: pmChoice,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error('Could not start bundled checkout — please try paying invoices individually.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start bundled checkout');
    } finally {
      setPayAllLoading(false);
    }
  };

  const [refreshKey, setRefreshKey] = useState(0);
  const [payInvoice, setPayInvoice] = useState(null);
  const fetchBookings = () => setRefreshKey((k) => k + 1);

  useEffect(() => { fetchBookings(); fetchPayAllSummary(); fetchPaymentMethods(); }, []);

  // iter263 — handle the saved-PM short-circuit redirect. When a
  // customer pays a lane booking via their saved card/ACH, the backend
  // returns a synthetic URL with ?saved_pm=1 (no Stripe Checkout). The
  // payment_intent webhook flips the booking to paid; we show a toast
  // and refresh after a beat.
  useEffect(() => {
    const savedPm = searchParams.get('saved_pm');
    if (savedPm !== '1') return;
    const status = searchParams.get('status');
    if (status === 'processing') {
      toast.info('Bank payment submitted — it may take 3–5 business days to settle.', { duration: 6000 });
    } else {
      toast.success('Payment successful — paid with your saved method.');
    }
    // Strip the query params so a refresh doesn't re-fire the toast.
    window.history.replaceState({}, '', '/customer/billing');
    setTimeout(() => {
      fetchBookings();
      fetchPayAllSummary();
    }, 1500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);


  return (
    <DashboardLayout title="Billing">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Receipt className="h-6 w-6 text-blue-600" />
            Billing & Receipts
          </h1>
          <p className="text-sm text-slate-500">
            Every invoice on your account — all types and statuses — with the full breakdown. Pay outstanding amounts online.
          </p>
        </div>

        {/* iter262 — Pay All Outstanding nudge. Only shown when the
            unified summary (covers all invoice types, not just lane
            bookings) reports 2+ open items. */}
        {payAllSummary.outstanding_count >= 2 && (
          <Card
            className="border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-emerald-100/60 shadow-sm"
            data-testid="cust-pay-all-banner"
          >
            <CardContent className="p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="rounded-full bg-emerald-600/10 p-2 shrink-0">
                    <DollarSign className="h-6 w-6 text-emerald-700" />
                  </div>
                  <div>
                    <p className="text-base sm:text-lg font-semibold text-emerald-900" data-testid="cust-pay-all-banner-headline">
                      You have {payAllSummary.outstanding_count} open invoice{payAllSummary.outstanding_count === 1 ? '' : 's'} totaling{' '}
                      <span className="tabular-nums">{formatMoney(payAllSummary.outstanding_total)}</span>
                    </p>
                    <p className="text-sm text-emerald-800/80 mt-0.5">
                      Settle everything in one checkout — saves time and avoids late notices.
                    </p>
                  </div>
                </div>
                <Button
                  size="lg"
                  onClick={() => { setPayAllPmChoice(null); setShowPayAllDialog(true); }}
                  disabled={payAllLoading}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm w-full sm:w-auto shrink-0"
                  data-testid="cust-pay-all-banner-cta"
                >
                  <DollarSign className="h-4 w-4 mr-1.5" />
                  Pay All Now
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}


        {/* iter320 — Payment Methods management card. Mirrors the
            parent Billing flow: list saved cards/ACH, badge the
            preferred one, link out to Stripe Customer Portal for
            add/edit/remove. */}
        <Card className="border border-slate-200" data-testid="cust-payment-methods-card">
          <CardContent className="p-4 lg:p-6">
            <div className="space-y-3 mb-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Wallet className="h-5 w-5 text-blue-600" />
                  <h2 className="text-base lg:text-lg font-semibold">Payment Methods</h2>
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white text-[10px] font-medium px-2 py-0.5"
                    data-testid="secured-by-stripe-badge"
                    title="Payments are processed securely by Stripe. Your full card number is never stored on our servers."
                  >
                    <Lock className="h-3 w-3" /> Secured by Stripe
                  </span>
                </div>
                <p className="text-xs lg:text-sm text-slate-500 mt-0.5">
                  {savedCard || savedAch
                    ? 'This card stays on file for your monthly auto-bill. Add or switch cards anytime — your saved method is never removed automatically.'
                    : 'Save a card or bank account so future bookings are one tap.'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => beginPaymentMethodSetup('card')} disabled={openingPaymentMethod} size="sm" className="bg-blue-600 hover:bg-blue-700 shrink-0 h-9 text-xs lg:text-sm" data-testid="cust-add-card-btn">
                  {openingPaymentMethod ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Opening…</> : <><CreditCard className="h-4 w-4 mr-2" />Add card</>}
                </Button>
                {achEnabled && <Button onClick={() => beginPaymentMethodSetup('us_bank_account')} disabled={openingPaymentMethod} size="sm" variant="outline" data-testid="cust-add-ach-btn"><Building2 className="h-4 w-4 mr-2" />Add bank</Button>}
              </div>
            </div>

            {paymentMethods.length === 0 && (
              <div
                className="mb-4 flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4"
                data-testid="cust-payment-method-required-banner"
              >
                <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold text-red-800">A payment method is required</p>
                  <p className="text-red-700 mt-0.5">
                    There is no card or bank account on file. Please add one to keep your
                    bookings and auto-payments active.
                  </p>
                </div>
              </div>
            )}

            {paymentMethods.length > 0 ? (
              <div className="space-y-2" data-testid="cust-payment-methods-list">
                {paymentMethods.map((pm) => (
                  <div
                    key={pm.id}
                    className={`flex items-center justify-between p-3 lg:p-4 rounded-lg border ${
                      pm.is_default ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-slate-50'
                    }`}
                    data-testid={`cust-payment-method-${pm.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {pm.type === 'card' ? (
                        <CreditCard className={`h-5 w-5 lg:h-6 lg:w-6 shrink-0 ${pm.is_default ? 'text-blue-600' : 'text-slate-500'}`} />
                      ) : (
                        <Building2 className={`h-5 w-5 lg:h-6 lg:w-6 shrink-0 ${pm.is_default ? 'text-blue-600' : 'text-slate-500'}`} />
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 text-sm lg:text-base truncate">
                          {pm.type === 'card'
                            ? `${(pm.brand || 'Card').charAt(0).toUpperCase()}${(pm.brand || 'card').slice(1)} •••• ${pm.last4}`
                            : `${pm.bank_name || 'Bank'} •••• ${pm.last4}`}
                        </p>
                        {pm.type === 'card' && pm.exp_month && (
                          <p className="text-xs text-slate-500">Expires {pm.exp_month}/{pm.exp_year}</p>
                        )}
                        {pm.type === 'bank_account' && pm.account_type && (
                          <p className="text-xs text-slate-500 capitalize">{pm.account_type} account</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {pm.is_default ? (
                        <span
                          className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full"
                          data-testid={`cust-pm-preferred-${pm.id}`}
                        >
                          Preferred
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSetDefaultPm(pm.id)}
                          data-testid={`cust-pm-make-preferred-${pm.id}`}
                        >
                          Make Preferred
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (window.confirm(
                            'Academy policy requires an active enrollment to keep a payment method on file. Remove this method only after adding a replacement, or after enrollment ends.'
                          )) {
                            removePaymentMethod(pm.id);
                          }
                        }}
                        data-testid={`cust-pm-remove-${pm.id}`}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 border border-dashed border-slate-200 rounded-lg">
                <Wallet className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-600 font-medium">No payment methods saved yet</p>
                <p className="text-xs text-slate-500 mt-1">
                  Add a card or bank account to skip checkout on future bookings.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="cust-invoices-card">
          <CardContent className="p-0">
            <ParentInvoiceList refreshKey={refreshKey} onPay={setPayInvoice} />
          </CardContent>
        </Card>
        <InvoicePayDialog invoice={payInvoice} onClose={() => setPayInvoice(null)}
          returnUrl={window.location.origin + '/customer/billing'} savedCard={savedCard} savedAch={savedAch} />

        {/* iter262 — Pay All chooser dialog. Mirrors parent flow:
            customer picks ACH (no fee) or Card (+3%), then is redirected
            to a single bundled Stripe Checkout for ALL outstanding rows. */}
        <Dialog open={showPayAllDialog} onOpenChange={setShowPayAllDialog}>
          <DialogContent className="max-w-md" data-testid="cust-pay-all-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-900">
                <DollarSign className="h-5 w-5" />
                Pay All Outstanding Invoices
              </DialogTitle>
              <DialogDescription>
                One Stripe checkout · {payAllSummary.outstanding_count} invoice{payAllSummary.outstanding_count === 1 ? '' : 's'} bundled together.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 flex items-center justify-between">
                <span className="text-sm text-slate-600">Subtotal</span>
                <span className="text-2xl font-bold text-slate-900 tabular-nums" data-testid="cust-pay-all-total">
                  {formatMoney(payAllSummary.outstanding_total)}
                </span>
              </div>

              <div className="text-xs text-slate-500">How would you like to pay?</div>

              <button
                type="button"
                onClick={() => setPayAllPmChoice('us_bank_account')}
                className={`w-full text-left p-3 rounded-lg border-2 transition flex items-center justify-between ${
                  payAllPmChoice === 'us_bank_account'
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-slate-200 hover:border-emerald-300 hover:bg-slate-50'
                }`}
                data-testid="cust-pay-all-ach"
              >
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-emerald-700" />
                  <div>
                    <div className="font-medium text-sm">Bank transfer (ACH)</div>
                    <div className="text-xs text-emerald-700">Save 3% — no processing fee</div>
                    {savedAch?.last4 && (
                      <div className="text-[11px] text-emerald-900/80 font-medium mt-0.5 flex items-center gap-1" data-testid="cust-pay-all-ach-saved">
                        <span className="inline-block w-1 h-1 rounded-full bg-emerald-700" />
                        Pays from {savedAch.bank_name || 'bank'} ••{savedAch.last4}
                      </div>
                    )}
                  </div>
                </div>
                <span className="text-sm font-semibold tabular-nums">{formatMoney(payAllSummary.outstanding_total)}</span>
              </button>

              <button
                type="button"
                onClick={() => setPayAllPmChoice('card')}
                className={`w-full text-left p-3 rounded-lg border-2 transition flex items-center justify-between ${
                  payAllPmChoice === 'card'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                }`}
                data-testid="cust-pay-all-card"
              >
                <div className="flex items-center gap-3">
                  <CreditCard className="h-5 w-5 text-blue-700" />
                  <div>
                    <div className="font-medium text-sm">Credit / debit card</div>
                    <div className="text-xs text-slate-500">Includes 3% processing fee</div>
                    {savedCard?.last4 && (
                      <div className="text-[11px] text-blue-900/80 font-medium mt-0.5 flex items-center gap-1" data-testid="cust-pay-all-card-saved">
                        <span className="inline-block w-1 h-1 rounded-full bg-blue-700" />
                        Pays from {savedCard.brand ? savedCard.brand.toUpperCase() : 'card'} ••{savedCard.last4}
                      </div>
                    )}
                  </div>
                </div>
                <span className="text-sm font-semibold tabular-nums">
                  {formatMoney(payAllSummary.outstanding_total * 1.03)}
                </span>
              </button>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowPayAllDialog(false)}
                disabled={payAllLoading}
                data-testid="cust-pay-all-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handlePayAll(payAllPmChoice)}
                disabled={payAllLoading || !payAllPmChoice}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="cust-pay-all-confirm"
              >
                {payAllLoading ? (
                  <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Loading…</>
                ) : (
                  <>Continue to Checkout <ArrowRight className="h-4 w-4 ml-1.5" /></>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={!!setupIntent} onOpenChange={(open) => { if (!open) setSetupIntent(null); }}>
          <DialogContent className="max-w-lg" data-testid="cust-payment-method-setup-dialog">
            <DialogHeader>
              <DialogTitle>Save payment method</DialogTitle>
              <DialogDescription>Your details are entered directly into Stripe's secure fields. VyomOS does not store card or bank-account numbers.</DialogDescription>
            </DialogHeader>
            {setupIntent && <PaymentMethodOnFileForm clientSecret={setupIntent.client_secret} publishableKey={setupIntent.publishable_key} onConfirmed={confirmPaymentMethodSetup} onError={(message) => toast.error(message)} />}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
